import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getAllAssessments,
  uploadEligibilityFile,
  getAssessmentEligibility,
  deleteAssessmentEligibility,
  deleteUserEligibility,
  Assessment,
  EligibilityData,
} from '../../services/AssessmentEligibilityService';
import { CircularProgress } from '@mui/material';
import './ManageAssessmentEligibility.css';
import { Layout } from '@/layout/Layout';
import { useAppTheme } from '@contexts/ThemeContext';
import * as XLSX from 'xlsx';

const ManageAssessmentEligibility: React.FC = () => {
  const { isDarkMode } = useAppTheme();
  const location = useLocation();
  const autoSelectedRef = useRef(false);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [selectedAssessment, setSelectedAssessment] = useState<Assessment | null>(null);
  const [eligibilityData, setEligibilityData] = useState<EligibilityData | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [manualEmails, setManualEmails] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [addingManual, setAddingManual] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'view' | 'add'>('view');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    userId: number | null;
    userName: string;
  }>({ isOpen: false, userId: null, userName: '' });
  const [resultModal, setResultModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'error';
    title: string;
    message: string;
    details?: {
      summary?: {
        totalEmailsProvided: number;
        successfullyAdded: number;
        alreadyEligible: number;
        notFound: number;
        deletedUsers: number;
      };
      added?: Array<{ email: string; name: string }>;
      alreadyEligible?: Array<{ email: string; name: string }>;
      notFound?: string[];
      deletedUsers?: Array<{ email: string; name: string }>;
    };
  }>({ isOpen: false, type: 'success', title: '', message: '', details: undefined });

  useEffect(() => {
    loadAssessments();
  }, []);

  const loadAssessments = async () => {
    setLoading(true);
    try {
      const response = await getAllAssessments();
      if (response.success && response.data) {
        setAssessments(response.data);
      } else {
        setMessage({ type: 'error', text: response.error as string || 'Failed to load assessments' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load assessments' });
    } finally {
      setLoading(false);
    }
  };

  const handleAssessmentSelect = async (assessment: Assessment) => {
    setSelectedAssessment(assessment);
    setFile(null);
    setMessage(null);
    setActiveTab('view'); // Switch to view tab when selecting assessment
    setSearchTerm(''); // Clear search
    setIsDropdownOpen(false); // Close dropdown

    // Load eligibility data for this assessment
    setLoading(true);
    try {
      const response = await getAssessmentEligibility(assessment.assessmentId);
      if (response.success && response.data) {
        setEligibilityData(response.data);
      } else {
        setEligibilityData(null);
      }
    } catch (error) {
      setEligibilityData(null);
    } finally {
      setLoading(false);
    }
  };

  // Auto-select assessment if navigated from Manage Assessments with state
  useEffect(() => {
    if (location.state?.assessmentId && assessments.length > 0 && !autoSelectedRef.current) {
      const targetAssessment = assessments.find(
        a => a.assessmentId === location.state.assessmentId
      );
      if (targetAssessment) {
        autoSelectedRef.current = true;
        handleAssessmentSelect(targetAssessment);
      }
    }
  }, [assessments, location.state]);

  // Filter assessments based on search term
  const filteredAssessments = assessments.filter(assessment =>
    assessment.assessmentTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
    assessment.course.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.search-select-wrapper')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();

      if (!['csv', 'xlsx', 'xls'].includes(fileExtension || '')) {
        setMessage({ type: 'error', text: 'Please select a CSV or Excel file' });
        return;
      }

      setFile(selectedFile);
      setMessage(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedAssessment || !file) {
      setResultModal({
        isOpen: true,
        type: 'error',
        title: 'Missing Information',
        message: 'Please select a file before uploading.',
        details: undefined
      });
      return;
    }

    setUploading(true);
    setMessage(null);

    try {
      const response = await uploadEligibilityFile(selectedAssessment.assessmentId, file);
      if (response.success && response.data) {
        const { summary, details } = response.data;

        // Determine overall success/partial success
        const hasAddedUsers = summary?.successfullyAdded > 0;
        const hasIssues = (summary?.alreadyEligible > 0) || (summary?.notFound > 0) || (summary?.deletedUsers > 0);

        let message = '';
        if (hasAddedUsers && !hasIssues) {
          message = `Successfully added ${summary.successfullyAdded} user${summary.successfullyAdded > 1 ? 's' : ''} to the eligibility list.`;
        } else if (hasAddedUsers && hasIssues) {
          message = `Added ${summary.successfullyAdded} user${summary.successfullyAdded > 1 ? 's' : ''}. Some emails were skipped - see details below.`;
        } else if (!hasAddedUsers && hasIssues) {
          message = 'No new users were added. All emails were either already eligible, not found, or belong to deleted users.';
        } else {
          message = 'Processed file successfully.';
        }

        setResultModal({
          isOpen: true,
          type: hasAddedUsers ? 'success' : 'error',
          title: hasAddedUsers ? 'File Processed Successfully' : 'No Users Added',
          message,
          details: {
            summary,
            ...details
          }
        });
        setFile(null);

        // Reload eligibility data
        const eligResponse = await getAssessmentEligibility(selectedAssessment.assessmentId);
        if (eligResponse.success && eligResponse.data) {
          setEligibilityData(eligResponse.data);
        }

        // Clear file input
        const fileInput = document.getElementById('file-input') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      } else {
        setResultModal({
          isOpen: true,
          type: 'error',
          title: 'Upload Failed',
          message: 'We couldn\'t process your file. Please ensure it\'s in the correct format (CSV or Excel) and try again.',
          details: undefined
        });
      }
    } catch (error) {
      setResultModal({
        isOpen: true,
        type: 'error',
        title: 'Operation Failed',
        message: 'An unexpected error occurred while uploading the file. Please check your connection and try again.',
        details: undefined
      });
    } finally {
      setUploading(false);
    }
  };

  const handleManualAdd = async () => {
    if (!selectedAssessment || !manualEmails.trim()) {
      setResultModal({
        isOpen: true,
        type: 'error',
        title: 'Missing Information',
        message: 'Please enter at least one email address before submitting.',
        details: undefined
      });
      return;
    }

    setAddingManual(true);
    setMessage(null);

    try {
      // Parse emails (comma or newline separated)
      const emailList = manualEmails
        .split(/[,\n]+/)
        .map(email => email.trim())
        .filter(email => email.length > 0);

      if (emailList.length === 0) {
        setResultModal({
          isOpen: true,
          type: 'error',
          title: 'Invalid Input',
          message: 'Please enter valid email addresses. Make sure emails are properly formatted.',
          details: undefined
        });
        setAddingManual(false);
        return;
      }

      // Create a Blob from the email list and upload as if it's a file
      const csvContent = 'email\n' + emailList.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const file = new File([blob], 'manual_emails.csv', { type: 'text/csv' });

      const response = await uploadEligibilityFile(selectedAssessment.assessmentId, file);
      if (response.success && response.data) {
        const { summary, details } = response.data;

        // Determine overall success/partial success
        const hasAddedUsers = summary?.successfullyAdded > 0;
        const hasIssues = (summary?.alreadyEligible > 0) || (summary?.notFound > 0) || (summary?.deletedUsers > 0);

        let message = '';
        if (hasAddedUsers && !hasIssues) {
          message = `Successfully added ${summary.successfullyAdded} user${summary.successfullyAdded > 1 ? 's' : ''} to the eligibility list.`;
        } else if (hasAddedUsers && hasIssues) {
          message = `Added ${summary.successfullyAdded} user${summary.successfullyAdded > 1 ? 's' : ''}. Some emails were skipped - see details below.`;
        } else if (!hasAddedUsers && hasIssues) {
          message = 'No new users were added. All emails were either already eligible, not found, or belong to deleted users.';
        } else {
          message = 'Processed emails successfully.';
        }

        setResultModal({
          isOpen: true,
          type: hasAddedUsers ? 'success' : 'error',
          title: hasAddedUsers ? 'Users Added Successfully' : 'No Users Added',
          message,
          details: {
            summary,
            ...details
          }
        });
        setManualEmails('');

        // Reload eligibility data
        const eligResponse = await getAssessmentEligibility(selectedAssessment.assessmentId);
        if (eligResponse.success && eligResponse.data) {
          setEligibilityData(eligResponse.data);
        }
      } else {
        setResultModal({
          isOpen: true,
          type: 'error',
          title: 'Unable to Add Users',
          message: 'We encountered an issue while adding users. Please verify the email addresses and try again.',
          details: undefined
        });
      }
    } catch (error) {
      setResultModal({
        isOpen: true,
        type: 'error',
        title: 'Operation Failed',
        message: 'An unexpected error occurred. Please check your connection and try again.',
        details: undefined
      });
    } finally {
      setAddingManual(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedAssessment(null);
    setEligibilityData(null);
    setSearchTerm('');
    setFile(null);
    setManualEmails('');
    setMessage(null);
    setActiveTab('view');
  };

  const handleRemoveUser = (userId: number, userName: string) => {
    setConfirmModal({ isOpen: true, userId, userName });
  };

  const handleConfirmDelete = async () => {
    if (!selectedAssessment || !confirmModal.userId) return;

    const userName = confirmModal.userName;
    // Close confirmation modal first
    setConfirmModal({ isOpen: false, userId: null, userName: '' });

    setLoading(true);
    try {
      const response = await deleteUserEligibility(selectedAssessment.assessmentId, confirmModal.userId);
      if (response.success) {
        // Show success modal
        setResultModal({
          isOpen: true,
          type: 'success',
          title: 'User Removed Successfully',
          message: `${userName} has been removed from the eligibility list and will no longer have access to this assessment.`,
          details: undefined
        });

        // Reload eligibility data
        const eligResponse = await getAssessmentEligibility(selectedAssessment.assessmentId);
        if (eligResponse.success && eligResponse.data) {
          setEligibilityData(eligResponse.data);
        }
      } else {
        // Show error modal with professional message
        setResultModal({
          isOpen: true,
          type: 'error',
          title: 'Unable to Remove User',
          message: 'We encountered an issue while trying to remove this user. Please try again later or contact support if the problem persists.',
          details: undefined
        });
      }
    } catch (error) {
      // Show error modal for exceptions
      setResultModal({
        isOpen: true,
        type: 'error',
        title: 'Operation Failed',
        message: 'An unexpected error occurred. Please check your connection and try again.',
        details: undefined
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setConfirmModal({ isOpen: false, userId: null, userName: '' });
  };

  const downloadSampleCSV = () => {
    const csvContent = 'email\nuser1@example.com\nuser2@example.com\nuser3@example.com';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_eligibility.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const downloadSampleExcel = () => {
    // Create sample data
    const data = [
      ['email'],
      ['user1@example.com'],
      ['user2@example.com'],
      ['user3@example.com']
    ];

    // Create a new workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(data);

    // Set column width for better readability
    ws['!cols'] = [{ wch: 30 }];

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Eligibility');

    // Generate Excel file and trigger download
    XLSX.writeFile(wb, 'sample_eligibility.xlsx');
  };

  return (
    <Layout>
      <div className="manage-eligibility-container" data-theme={isDarkMode ? 'dark' : 'light'}>
        {/* Global Loading Overlay */}
        {(loading || uploading || addingManual) && (
          <div className="eligibility-loading-overlay">
            <CircularProgress size={60} thickness={4} sx={{ color: '#667eea' }} />
          </div>
        )}

        {/* Header */}
        <div className="eligibility-header">
          <h1>Manage Assessment Eligibility</h1>
          <p>Control which users can see specific assessments by uploading email lists</p>
        </div>

        {/* Message */}
        {message && (
          <div className={`message ${message.type}`}>
            {message.text}
            <button className="close-btn" onClick={() => setMessage(null)}>×</button>
          </div>
        )}

        {/* Assessment Selector */}
        <div className="assessment-selector-section">
          <label className="selector-label">Select Assessment</label>
          <div className="search-select-wrapper">
            <input
              type="text"
              className="assessment-search"
              placeholder={selectedAssessment ? selectedAssessment.assessmentTitle : "Click to select an assessment..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setIsDropdownOpen(true)}
              onClick={() => setIsDropdownOpen(true)}
            />
            {selectedAssessment ? (
              <svg
                className="clear-icon"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                onClick={handleClearSelection}
              >
                <title>Clear selection</title>
                <circle cx="10" cy="10" r="9" fill="currentColor" opacity="0.1"/>
                <path d="M6 6L14 14M14 6L6 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            ) : (
              <svg
                className="dropdown-icon"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
            {isDropdownOpen && filteredAssessments.length > 0 && (
              <div className="assessment-dropdown">
                {filteredAssessments.map((assessment) => (
                  <div
                    key={assessment.assessmentId}
                    className={`dropdown-item ${selectedAssessment?.assessmentId === assessment.assessmentId ? 'selected' : ''}`}
                    onClick={() => handleAssessmentSelect(assessment)}
                  >
                    <div className="dropdown-item-title">{assessment.assessmentTitle}</div>
                    <div className="dropdown-item-meta">
                      <span>{assessment.course}</span>
                      <span className={`status-badge ${assessment.status}`}>{assessment.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Show placeholder only when no assessment is selected */}
          {!selectedAssessment && (
            <div className="select-placeholder">
              <p>Select an assessment to manage eligibility settings</p>
              <p className="total-count">{assessments.length} assessment{assessments.length !== 1 ? 's' : ''} available</p>
            </div>
          )}
        </div>

        {/* Selected Assessment Info & Tabs */}
        {selectedAssessment && (
          <div className="selected-assessment-section">
            {/* Assessment Info Card */}
            <div className="assessment-info-card">
              <div className="info-header">
                <h2>{selectedAssessment.assessmentTitle}</h2>
                <span className={`status-badge ${selectedAssessment.status}`}>
                  {selectedAssessment.status}
                </span>
              </div>
              <div className="info-details">
                <div className="info-item">
                  <span className="info-label">Course:</span>
                  <span className="info-value">{selectedAssessment.course}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Goal:</span>
                  <span className="info-value">{selectedAssessment.goal}</span>
                </div>
                {eligibilityData && (
                  <div className="info-item">
                    <span className="info-label">Eligible Users:</span>
                    <span className="info-value highlight">{eligibilityData.totalUsers} users</span>
                  </div>
                )}
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="tab-navigation">
              <button
                className={`tab-btn ${activeTab === 'view' ? 'active' : ''}`}
                onClick={() => setActiveTab('view')}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 3C4.5 3 1.73 5.11 1 8c.73 2.89 3.5 5 7 5s6.27-2.11 7-5c-.73-2.89-3.5-5-7-5zm0 8.5c-1.93 0-3.5-1.57-3.5-3.5S6.07 4.5 8 4.5s3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z" fill="currentColor"/>
                  <circle cx="8" cy="8" r="2" fill="currentColor"/>
                </svg>
                View Eligible Users
              </button>
              <button
                className={`tab-btn ${activeTab === 'add' ? 'active' : ''}`}
                onClick={() => setActiveTab('add')}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Add Users
              </button>
            </div>

            {/* Tab Content */}
            <div className="tab-content">
              {activeTab === 'view' && (
                <div className="view-users-tab">
                  <h3>Eligible Users List</h3>
                  {loading ? (
                    <div className="loading-spinner">Loading users...</div>
                  ) : eligibilityData && eligibilityData.totalUsers > 0 ? (
                    <>
                      <div className="users-stats">
                        <div className="stat-card">
                          <div className="stat-number">{eligibilityData.totalUsers}</div>
                          <div className="stat-label">Total Eligible Users</div>
                        </div>
                      </div>
                      <div className="users-table-wrapper">
                        <table className="users-table">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Added On</th>
                              <th className="action-column">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {eligibilityData.users.map((user, index) => (
                              <tr key={user.id}>
                                <td>{index + 1}</td>
                                <td>{user.user_name || '-'}</td>
                                <td>{user.user_email}</td>
                                <td>{new Date(user.uploaded_at).toLocaleDateString()}</td>
                                <td className="action-column">
                                  <button
                                    className="delete-user-btn"
                                    onClick={() => handleRemoveUser(user.user_id, user.user_name)}
                                    title="Remove user from eligibility list"
                                  >
                                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M4 5h10M7 8v4M11 8v4M5 5l1 9c0 .6.4 1 1 1h4c.6 0 1-.4 1-1l1-9M7 5V3c0-.6.4-1 1-1h2c.6 0 1 .4 1 1v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <div className="empty-state">
                      <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="32" cy="32" r="30" stroke="#CBD5E0" strokeWidth="2" strokeDasharray="4 4"/>
                        <path d="M32 20v24M20 32h24" stroke="#CBD5E0" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      <h4>No Users Added Yet</h4>
                      <p>Add emails manually or upload a file to grant access to this assessment.</p>
                      <button className="btn-primary" onClick={() => setActiveTab('add')}>
                        Add Users Now
                      </button>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'add' && (
                <div className="add-users-tab">
                  <h3>Add Users to Eligibility List</h3>

                  {/* Manual Email Input */}
                  <div className="add-method-card">
                    <div className="method-header">
                      <h4>Add Email Addresses Manually</h4>
                      <span className="method-badge">Quick Add</span>
                    </div>
                    <p className="method-description">
                      Enter one or more email addresses separated by commas or new lines
                    </p>
                    <textarea
                      className="email-input"
                      placeholder="user1@example.com, user2@example.com&#10;or one email per line"
                      value={manualEmails}
                      onChange={(e) => setManualEmails(e.target.value)}
                      rows={5}
                      disabled={addingManual}
                    />
                    <button
                      className="btn-primary"
                      onClick={handleManualAdd}
                      disabled={!manualEmails.trim() || addingManual}
                    >
                      {addingManual ? 'Adding...' : 'Add Emails'}
                    </button>
                  </div>

                  {/* Divider */}
                  <div className="divider">
                    <span>OR</span>
                  </div>

                  {/* File Upload */}
                  <div className="add-method-card">
                    <div className="method-header">
                      <h4>Upload Eligibility File</h4>
                      <span className="method-badge">Bulk Upload</span>
                    </div>
                    <p className="method-description">
                      Upload a CSV or Excel file containing user emails. Only users in this file will be able to see this assessment.
                    </p>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <button className="btn-link" onClick={downloadSampleCSV}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8 11V3M8 11l3-3M8 11L5 8M2 13h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Download Sample CSV
                      </button>
                      <button className="btn-link" onClick={downloadSampleExcel}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8 11V3M8 11l3-3M8 11L5 8M2 13h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Download Sample Excel
                      </button>
                    </div>

                    <div className="file-upload-area">
                      <input
                        id="file-input"
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={handleFileChange}
                        disabled={uploading}
                      />
                      {file && (
                        <div className="file-selected">
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 0L12.5 7.5L20 10L12.5 12.5L10 20L7.5 12.5L0 10L7.5 7.5L10 0Z" fill="#48BB78"/>
                          </svg>
                          <span>Selected: {file.name}</span>
                        </div>
                      )}
                    </div>

                    <button
                      className="btn-primary"
                      onClick={handleUpload}
                      disabled={!file || uploading}
                    >
                      {uploading ? 'Uploading...' : 'Upload & Apply'}
                    </button>
                  </div>

                  {/* Info Note */}
                  <div className="info-note">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="10" cy="10" r="9" stroke="#3182CE" strokeWidth="2"/>
                      <path d="M10 6v4M10 14h.01" stroke="#3182CE" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    <div>
                      <strong>Note:</strong> Uploading a new file will replace all existing users for this assessment.
                      Make sure your file includes all users who should have access.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {confirmModal.isOpen && (
          <div className="modal-overlay" onClick={handleCancelDelete}>
            <div className="confirmation-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <svg width="36" height="36" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="24" cy="24" r="22" fill="#FEE2E2" stroke="#EF4444" strokeWidth="2"/>
                  <path d="M24 16v12M24 32h.01" stroke="#EF4444" strokeWidth="3" strokeLinecap="round"/>
                </svg>
                <h3>Remove User Access</h3>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to remove <strong>{confirmModal.userName || 'this user'}</strong> from the eligibility list?</p>
                <p className="modal-warning">This user will no longer be able to access this assessment.</p>
              </div>
              <div className="modal-actions">
                <button className="btn-cancel" onClick={handleCancelDelete}>
                  Cancel
                </button>
                <button className="btn-confirm-delete" onClick={handleConfirmDelete}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 4h10M6 7v4M10 7v4M4 4l1 9c0 .6.4 1 1 1h4c.6 0 1-.4 1-1l1-9M6 4V2c0-.6.4-1 1-1h2c.6 0 1 .4 1 1v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Remove User
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Result Modal (Success/Error) */}
        {resultModal.isOpen && (
          <div className="modal-overlay" onClick={() => setResultModal({ ...resultModal, isOpen: false })}>
            <div className="result-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                {resultModal.type === 'success' ? (
                  <svg width="36" height="36" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="24" cy="24" r="22" fill="#D4EDDA" stroke="#48BB78" strokeWidth="2"/>
                    <path d="M14 24l8 8 16-16" stroke="#48BB78" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="36" height="36" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="24" cy="24" r="22" fill="#FEE2E2" stroke="#EF4444" strokeWidth="2"/>
                    <path d="M18 18l12 12M30 18l-12 12" stroke="#EF4444" strokeWidth="3" strokeLinecap="round"/>
                  </svg>
                )}
                <h3>{resultModal.title}</h3>
              </div>
              <div className="modal-body">
                <p>{resultModal.message}</p>

                {/* Display detailed validation results if available */}
                {resultModal.details?.summary && (
                  <div className="validation-details">
                    {/* Summary Cards */}
                    {resultModal.details.summary.successfullyAdded > 0 && (
                      <div className="detail-card success-card">
                        <div className="detail-icon">✓</div>
                        <div className="detail-content">
                          <div className="detail-count">{resultModal.details.summary.successfullyAdded}</div>
                          <div className="detail-label">Successfully Added</div>
                        </div>
                      </div>
                    )}

                    {resultModal.details.summary.alreadyEligible > 0 && (
                      <div className="detail-card info-card">
                        <div className="detail-icon">ℹ</div>
                        <div className="detail-content">
                          <div className="detail-count">{resultModal.details.summary.alreadyEligible}</div>
                          <div className="detail-label">Already Eligible</div>
                        </div>
                      </div>
                    )}

                    {resultModal.details.summary.notFound > 0 && (
                      <div className="detail-card warning-card">
                        <div className="detail-icon">!</div>
                        <div className="detail-content">
                          <div className="detail-count">{resultModal.details.summary.notFound}</div>
                          <div className="detail-label">Not Found</div>
                        </div>
                      </div>
                    )}

                    {resultModal.details.summary.deletedUsers > 0 && (
                      <div className="detail-card error-card">
                        <div className="detail-icon">✕</div>
                        <div className="detail-content">
                          <div className="detail-count">{resultModal.details.summary.deletedUsers}</div>
                          <div className="detail-label">Deleted Users</div>
                        </div>
                      </div>
                    )}

                    {/* Detailed Lists */}
                    {resultModal.details.alreadyEligible && resultModal.details.alreadyEligible.length > 0 && (
                      <div className="detail-list">
                        <h5>Already Eligible Users:</h5>
                        <ul>
                          {resultModal.details.alreadyEligible.map((user, idx) => (
                            <li key={idx}>{user.name} ({user.email})</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {resultModal.details.notFound && resultModal.details.notFound.length > 0 && (
                      <div className="detail-list">
                        <h5>Email Addresses Not Found:</h5>
                        <ul>
                          {resultModal.details.notFound.map((email, idx) => (
                            <li key={idx}>{email}</li>
                          ))}
                        </ul>
                        <p className="detail-note">These email addresses do not exist in the system.</p>
                      </div>
                    )}

                    {resultModal.details.deletedUsers && resultModal.details.deletedUsers.length > 0 && (
                      <div className="detail-list">
                        <h5>Deleted User Accounts:</h5>
                        <ul>
                          {resultModal.details.deletedUsers.map((user, idx) => (
                            <li key={idx}>{user.name} ({user.email})</li>
                          ))}
                        </ul>
                        <p className="detail-note">These users have been deactivated and cannot be added.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-actions">
                <button
                  className={resultModal.type === 'success' ? 'btn-success' : 'btn-error'}
                  onClick={() => setResultModal({ ...resultModal, isOpen: false })}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ManageAssessmentEligibility;
